import { AxiosResponse } from 'axios';
import { action, computed, observable } from 'mobx';
import { Notice } from '../CommonInterfaces';
import * as React from 'react';
import { IFormHelperProps, TGridColumnWidth } from '@teliaee/design-system';
import { isSmallModuleView } from '../util/ModuleUtil';
import { TranslationParameterMapping } from './TranslateStore';
import { ERRORED_HELPER_FIELD_ID_SUFFIX } from '../util/FieldHelperUtil';
import { isInWebView } from '../util/BrowserUtil';

export default class UiStore {
  @observable accessor notices: Notice[] = [];
  @observable accessor selectedTab: string | null = null;
  @observable accessor componentWidth: number = -1;

  @action.bound
  public setComponentWidth(width: number) {
    this.componentWidth = width;
  }

  @computed
  get hasNotice() {
    return this.notices && !!this.notices?.length;
  }

  @action.bound
  public showSuccessNotice(message: string, paramObject?: TranslationParameterMapping) {
    this.notices = [{ children: message, type: 'success', paramObject }];
  }

  @action.bound
  public showErrorNoticeFromResponse(response: AxiosResponse | undefined) {
    this.notices = [this.getNoticeFromResponseIfExists(response) || { children: 'error.general', type: 'error' }];
  }

  @action.bound
  public showErrorNotice(message: React.ReactNode, renderedMessage?: string, paramObject?: TranslationParameterMapping, erroredFieldIds?: string[]) {
    if (!!message || !!renderedMessage) {
      this.notices = [{ children: message, renderedMessage: renderedMessage, type: 'error', paramObject, erroredFieldIds }];
    }
  }

  @action.bound
  public showErrorNoticeWithErroredFields(message: React.ReactNode, erroredFields: (IFormHelperProps | undefined)[]) {
    const erroredFieldsIds = erroredFields
      .filter((helper): helper is IFormHelperProps => helper !== undefined)
      .map((helper) => helper.id)
      .filter((fieldId): fieldId is string => fieldId !== undefined);

    const cleanedErroredFieldsIds = erroredFieldsIds.map((fieldId) => fieldId.replace(ERRORED_HELPER_FIELD_ID_SUFFIX, ''));

    this.showErrorNotice(message, undefined, undefined, cleanedErroredFieldsIds);
  }

  @action.bound
  public showCustomNotice(title: string, children: React.ReactNode) {
    if (children && title) {
      this.notices = [{ children: title, jsx: children }];
    }
  }

  @action.bound
  public handleNotice(response?: AxiosResponse, clearEmptyNotice: boolean = true) {
    if (clearEmptyNotice) {
      this.hideNotices();
    }
    const noticeFromResponse = this.getNoticeFromResponseIfExists(response);
    if (noticeFromResponse) {
      this.notices = [noticeFromResponse];
    }
  }

  @action.bound
  public hideNotices() {
    this.notices = [];
  }

  @action.bound
  public setSelectedTab(tab: string | null) {
    this.selectedTab = tab;
  }

  public getLoginPanelGridWidth = (): TGridColumnWidth[] => {
    const lgWidth = isSmallModuleView(this.componentWidth) ? '12' : '4';

    return ['sm-8', 'md-6', `lg-${lgWidth}` as TGridColumnWidth];
  };

  private handleCsrfErrorByNavigator(notice: Notice) {
    const csrfError = 'error.csrf_token_invalid';
    if (notice.message === csrfError) {
      notice.message = isInWebView() ? `${csrfError}_app` : `${csrfError}_browser`;
    }
  }

  private getNoticeFromResponseIfExists(response?: AxiosResponse) {
    if (response?.data?.notice) {
      const notice = response.data.notice;
      this.handleCsrfErrorByNavigator(notice);
      return { ...notice, children: notice.message, erroredFieldIds: !!notice.failedFieldId ? [notice.failedFieldId] : undefined };
    }
    return null;
  }
}
